import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Ban, CheckCheck, X, Mail } from "lucide-react";
import { api } from "../lib/api";
import { formatDate } from "../lib/constants";

export const NotificationsBell = ({ compact }) => {
  const [data, setData] = useState({ items: [], non_lette: 0 });
  const [open, setOpen] = useState(false);
  const seen = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/notifications", { params: { limit: 20 } });
      setData(d);
      if (seen.current !== null && d.non_lette > seen.current) {
        const { toast } = await import("sonner");
        const latest = d.items.find((n) => !n.letto);
        if (latest) toast.warning(latest.titolo, { description: latest.messaggio, action: latest.link ? { label: "Apri", onClick: () => navigate(latest.link) } : undefined });
      }
      seen.current = d.non_lette;
    } catch (e) { /* silent */ }
  }, [navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const openItem = async (n) => {
    if (!n.letto) api.post(`/notifications/${n.id}/read`).then(load);
    setOpen(false);
    if (n.link) navigate(n.link);
    else if (n.content_id) navigate(`/contenuti/${n.content_id}`);
  };
  const remove = async (e, n) => {
    e.stopPropagation();
    await api.delete(`/notifications/${n.id}`);
    load();
  };

  return (
    <div className="relative">
      <button data-testid="notifications-bell" onClick={() => setOpen(!open)} className={`relative flex items-center gap-3 rounded-lg text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-white ${compact ? "p-2" : "w-full px-3 py-2.5"}`}>
        <Bell className={compact ? "h-5 w-5" : "h-4 w-4"} />
        {!compact && "Notifiche"}
        {data.non_lette > 0 && (
          <span data-testid="notifications-unread" className={`absolute flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-black ${compact ? "-right-0.5 -top-0.5" : "right-3 top-2"}`}>{data.non_lette}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notifications-panel" className={`absolute z-50 w-[min(92vw,380px)] rounded-xl border border-white/10 bg-[#0F1626] p-2 shadow-2xl ${compact ? "right-0 top-12" : "bottom-12 left-0"}`}>
            <div className="flex items-center justify-between px-3 py-2">
              <p className="text-sm font-semibold">Notifiche</p>
              {data.non_lette > 0 && <button data-testid="notifications-read-all" onClick={readAll} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-white"><CheckCheck className="h-3.5 w-3.5" /> Segna tutte lette</button>}
            </div>
            <ul className="max-h-[60vh] space-y-1 overflow-y-auto">
              {data.items.length === 0 && <li className="px-3 py-6 text-center text-xs text-muted-foreground">Nessuna notifica.</li>}
              {data.items.map((n) => (
                <li key={n.id} data-testid={`notification-${n.id}`} onClick={() => openItem(n)} className={`group flex cursor-pointer items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-secondary ${n.letto ? "opacity-60" : ""}`}>
                  {n.tipo === "followup" ? <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : n.tipo === "non_pertinente" ? <Ban className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold">{n.titolo}</p>
                    <p className="mt-0.5 line-clamp-3 text-xs text-muted-foreground">{n.messaggio}</p>
                    <p className="mt-1 text-[10px] font-mono text-muted-foreground">{formatDate(n.created_at)}</p>
                  </div>
                  <button data-testid={`notification-delete-${n.id}`} onClick={(e) => remove(e, n)} className="rounded p-1 text-muted-foreground hover:text-white"><X className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
